import { fetchItemsAPI } from "../api/itemsApi";
import { getSystemRelationSource } from "../utils/systemRelationSources";

export type RelationFieldMeta = {
  name: string;
  type: string;
  value?: string;
  fields?: unknown;
};

function parseFieldKeysForExport(fields: unknown): string[] {
  if (!fields) return [];
  if (Array.isArray(fields)) return fields.map((f) => String(f).trim()).filter(Boolean);
  if (typeof fields === "string") return fields.split(",").map((f) => f.trim()).filter(Boolean);
  return [];
}

function getNestedValueForExport(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== "object") return undefined;
  if (!path.includes(".")) return (obj as Record<string, unknown>)[path];
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object") return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

/** Label for relation picker (same basis as import lookup labels). */
export function relationItemPickerLabel(item: Record<string, unknown>, fieldMeta: RelationFieldMeta): string {
  const uidRaw = item.uid ?? item.id;
  const uid = uidRaw != null ? String(uidRaw) : "";
  const built = buildLabelFromFieldsForExport(item, fieldMeta.fields);
  const label =
    built ||
    (item.name != null ? String(item.name) : "") ||
    (item.title != null ? String(item.title) : "") ||
    (item.label != null ? String(item.label) : "") ||
    (item.code != null && String(item.code).trim() ? String(item.code) : "") ||
    uid;
  return uid ? `${label || uid} (${uid})` : label || "—";
}

export function buildLabelFromFieldsForExport(item: Record<string, unknown>, fieldsToUse: unknown): string {
  const keys = parseFieldKeysForExport(fieldsToUse);
  if (!keys.length) return "";
  const parts = keys
    .map((k) => getNestedValueForExport(item, k))
    .filter((v) => v !== undefined && v !== null && String(v).trim() !== "");
  return parts.map((p) => String(p)).join(" ").trim();
}

export async function fetchAllRelatedItems(
  relationKey: string,
  fieldType: string
): Promise<Record<string, unknown>[]> {
  if (fieldType === "systemrelation") {
    const src = getSystemRelationSource(relationKey);
    if (!src) return [];
    const raw = await src.fetchItems();
    return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : [];
  }
  const slugsToTry =
    typeof relationKey === "string" && relationKey.includes("_")
      ? [relationKey, relationKey.split("_")[0]]
      : [relationKey];
  const out: Record<string, unknown>[] = [];
  for (const slugForApi of slugsToTry) {
    try {
      let page = 1;
      const limit = 100;
      for (; ;) {
        const result = await fetchItemsAPI(slugForApi, { aclAction: "relate", page, limit });
        const items = result?.items ?? [];
        out.push(...(Array.isArray(items) ? (items as Record<string, unknown>[]) : []));
        if (items.length < limit) break;
        page += 1;
      }
      return out;
    } catch {
      /* try next slug */
    }
  }
  return [];
}

export type RelationLookup = {
  byUid: Map<string, string>;
  byLabel: Map<string, string>;
};

export function buildRelationLookup(items: Record<string, unknown>[], fieldMeta: RelationFieldMeta): RelationLookup {
  const byUid = new Map<string, string>();
  const byLabel = new Map<string, string>();
  const fieldsToUse = fieldMeta.fields;
  const registerLabel = (raw: string, uid: string) => {
    const low = raw.trim().toLowerCase();
    if (!low) return;
    if (!byLabel.has(low)) byLabel.set(low, uid);
  };
  for (const item of items) {
    const uidRaw = item.uid ?? item.id;
    if (uidRaw == null) continue;
    const uid = String(uidRaw);
    byUid.set(uid.toLowerCase(), uid);
    byUid.set(uid, uid);
    const built = buildLabelFromFieldsForExport(item, fieldsToUse);
    const label =
      built ||
      (item.name != null ? String(item.name) : "") ||
      (item.title != null ? String(item.title) : "") ||
      (item.label != null ? String(item.label) : "") ||
      uid;
    registerLabel(label, uid);
    if (item.code != null && String(item.code).trim()) {
      registerLabel(String(item.code), uid);
    }
  }
  return { byUid, byLabel };
}

export function resolveRelationValue(
  raw: string,
  lookup: RelationLookup
): { uid: string | null; message: string | null } {
  const t = raw.trim();
  if (!t) return { uid: null, message: "Value is empty" };
  const low = t.toLowerCase();
  if (lookup.byUid.has(t)) return { uid: lookup.byUid.get(t)!, message: null };
  if (lookup.byUid.has(low)) return { uid: lookup.byUid.get(low)!, message: null };
  if (lookup.byLabel.has(low)) return { uid: lookup.byLabel.get(low)!, message: null };
  return { uid: null, message: `No row in the related collection matches "${t}"` };
}

export function isRelationNoMatchError(message: string | null | undefined): boolean {
  if (!message) return false;
  return message.includes("No row in the related collection matches");
}

/** Empty cell → no uid, no error (optional relation). */
export function resolveRelationValueForImport(
  raw: string,
  lookup: RelationLookup
): { uid: string | null; error: string | null } {
  const t = raw.trim();
  if (!t) return { uid: null, error: null };
  const r = resolveRelationValue(t, lookup);
  if (r.message) return { uid: null, error: r.message };
  return { uid: r.uid, error: null };
}

export function resolveMultiRelationValueForImport(
  raw: string,
  lookup: RelationLookup
): { uids: string[] | null; error: string | null } {
  const t = raw.trim();
  if (!t) return { uids: null, error: null };
  const parts = t.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return { uids: null, error: null };
  const uids: string[] = [];
  for (const p of parts) {
    const { uid, message } = resolveRelationValue(p, lookup);
    if (message) return { uids: null, error: `${p}: ${message}` };
    if (uid) uids.push(uid);
  }
  return { uids, error: null };
}
